import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { bulkDescriptionUpload, downloadSampleDescriptionCsv } from '../../../services/bulkInstituteContentService';

const BulkDescriptionUploadModal = ({ isOpen, onClose, onSuccess }) => {
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(null);
  const [result, setResult] = useState(null);
  const [fatalError, setFatalError] = useState(null);
  const fileInputRef = useRef(null);

  if (!isOpen) return null;

  const resetState = () => { setFile(null); setProgress(null); setResult(null); setFatalError(null); };

  const handleClose = () => { if (uploading) return; resetState(); onClose?.(); };

  const handleFileSelect = (selectedFile) => {
    if (!selectedFile) return;
    const ext = selectedFile?.name?.split('.')?.pop()?.toLowerCase();
    if (ext !== 'csv') { setFatalError('Please upload a CSV file (.csv)'); return; }
    if (selectedFile?.size > 10 * 1024 * 1024) { setFatalError('File size must be under 10 MB'); return; }
    setFile(selectedFile); setFatalError(null); setResult(null);
  };

  const handleDrop = (e) => { e?.preventDefault(); setDragOver(false); handleFileSelect(e?.dataTransfer?.files?.[0]); };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true); setResult(null); setFatalError(null);
    setProgress({ phase: 'parsing', rowIndex: 0, totalRows: 0 });
    try {
      const uploadResult = await bulkDescriptionUpload(file, { onProgress: (p) => setProgress(p) });
      if (uploadResult?.fatalError) { setFatalError(uploadResult?.fatalError); }
      else { setResult(uploadResult); if (uploadResult?.summary?.totalRowsUploaded > 0) onSuccess?.(); }
    } catch (err) {
      setFatalError(err?.message || 'An unexpected error occurred');
    } finally { setUploading(false); setProgress(null); }
  };

  const progressLabel = () => {
    if (!progress) return '';
    if (progress?.phase === 'parsing') return 'Reading CSV file…';
    if (progress?.phase === 'uploading') {
      if (progress?.instituteName) return `Updating "${progress?.instituteName}, ${progress?.city}" — row ${progress?.rowIndex}/${progress?.totalRows}`;
      return `Processing row ${progress?.rowIndex}/${progress?.totalRows}`;
    }
    return 'Processing…';
  };

  const progressPercent = () => {
    if (!progress || !progress?.totalRows) return 5;
    if (progress?.phase === 'parsing') return 10;
    return Math.min(99, Math.round(10 + (progress?.rowIndex / progress?.totalRows) * 89));
  };

  const { summary, results } = result || {};

  const statusColor = (status) => {
    if (status === 'success') return 'bg-success';
    if (status === 'not_found') return 'bg-warning';
    return 'bg-error';
  };

  const statusLabel = (status) => {
    if (status === 'success') return 'Updated';
    if (status === 'not_found') return 'Not Found';
    return 'Failed';
  };

  const confirmationBanner = () => {
    if (!summary) return null;
    const { totalRowsUploaded, totalRowsFailed, totalRowsSkipped, totalRows } = summary;
    if (totalRowsUploaded === totalRows) {
      return (
        <div className="flex items-start gap-3 p-4 bg-success/10 border border-success/40 rounded-lg">
          <Icon name="CheckCircle2" size={20} color="var(--color-success)" className="flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-success">Upload Complete!</p>
            <p className="text-sm text-success/80 mt-0.5">{totalRowsUploaded} institute description{totalRowsUploaded !== 1 ? 's' : ''} updated successfully.</p>
          </div>
        </div>
      );
    }
    if (totalRowsUploaded > 0) {
      return (
        <div className="flex items-start gap-3 p-4 bg-warning/10 border border-warning/40 rounded-lg">
          <Icon name="AlertTriangle" size={20} color="var(--color-warning)" className="flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-warning">Upload Completed with Issues</p>
            <p className="text-sm text-warning/80 mt-0.5">
              {totalRowsUploaded} updated · {totalRowsFailed} failed · {totalRowsSkipped} not found
            </p>
          </div>
        </div>
      );
    }
    return (
      <div className="flex items-start gap-3 p-4 bg-error/10 border border-error/40 rounded-lg">
        <Icon name="XCircle" size={20} color="var(--color-error)" className="flex-shrink-0 mt-0.5" />
        <div>
          <p className="font-semibold text-error">Upload Failed</p>
          <p className="text-sm text-error/80 mt-0.5">No descriptions were updated. Check the errors below.</p>
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-secondary/10 flex items-center justify-center">
              <Icon name="FileText" size={18} color="var(--color-secondary)" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-foreground">Bulk Description / About Us Upload</h2>
              <p className="text-xs text-muted-foreground">Update institute descriptions — matched by Name + City</p>
            </div>
          </div>
          <button onClick={handleClose} disabled={uploading} className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors disabled:opacity-40">
            <Icon name="X" size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">

          {!result && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex gap-3">
                <Icon name="Info" size={16} color="#3b82f6" className="flex-shrink-0 mt-0.5" />
                <div className="text-sm text-blue-700 space-y-1">
                  <p className="font-semibold">How it works</p>
                  <ul className="text-blue-600 space-y-1 list-disc list-inside">
                    <li>Each row updates the <strong>description / About Us</strong> for the matched institute</li>
                    <li>Institute is identified by <strong>institute_name</strong> + <strong>city</strong></li>
                    <li>Existing description will be <strong>overwritten</strong> with the new value</li>
                    <li>Download the sample template to see the exact format</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {!result && (
            <div className="bg-muted/30 border border-border rounded-lg p-4">
              <p className="text-xs font-semibold text-foreground mb-2">Required Columns</p>
              <div className="flex flex-wrap gap-1.5">
                {['institute_name', 'city', 'description']?.map((col) => (
                  <span key={col} className="text-xs bg-secondary/10 text-secondary px-2 py-0.5 rounded font-mono">{col}</span>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-2">The <code className="bg-muted px-1 rounded">description</code> column maps to the institute's About Us / description field.</p>
            </div>
          )}

          {!result && (
            <div className="flex items-center justify-between bg-success/5 border border-success/30 rounded-lg px-4 py-3">
              <div className="flex items-center gap-3">
                <Icon name="Download" size={16} color="var(--color-success)" />
                <div>
                  <p className="text-sm font-medium text-foreground">Download Sample CSV Template</p>
                  <p className="text-xs text-muted-foreground">Includes 2 sample rows with Amrita &amp; Anna University examples</p>
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={downloadSampleDescriptionCsv}>Download</Button>
            </div>
          )}

          {/* File drop zone */}
          {!result && (
            <div
              onDragOver={(e) => { e?.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef?.current?.click()}
              className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${dragOver ? 'border-secondary bg-secondary/5' : 'border-border hover:border-secondary/50 hover:bg-muted/20'}`}
            >
              <input ref={fileInputRef} type="file" accept=".csv" className="hidden" onChange={(e) => handleFileSelect(e?.target?.files?.[0])} />
              <Icon name="Upload" size={32} color="var(--color-muted-foreground)" className="mx-auto mb-3" />
              {file ? (
                <div>
                  <p className="font-medium text-foreground">{file?.name}</p>
                  <p className="text-sm text-muted-foreground mt-1">{(file?.size / 1024)?.toFixed(1)} KB — click to change</p>
                </div>
              ) : (
                <div>
                  <p className="font-medium text-foreground">Drop your CSV here or click to browse</p>
                  <p className="text-sm text-muted-foreground mt-1">CSV files only · max 10 MB</p>
                </div>
              )}
            </div>
          )}

          {fatalError && (
            <div className="flex items-start gap-3 p-4 bg-error/10 border border-error/30 rounded-lg">
              <Icon name="AlertCircle" size={18} color="var(--color-error)" className="flex-shrink-0 mt-0.5" />
              <p className="text-sm text-error">{fatalError}</p>
            </div>
          )}

          {uploading && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">{progressLabel()}</span>
                <span className="font-medium text-foreground">{progressPercent()}%</span>
              </div>
              <div className="h-2 bg-muted rounded-full overflow-hidden">
                <div className="h-full bg-secondary rounded-full transition-all duration-300" style={{ width: `${progressPercent()}%` }} />
              </div>
            </div>
          )}

          {result && (
            <div className="space-y-4">
              {confirmationBanner()}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-success/10 border border-success/30 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-success">{summary?.totalRowsUploaded}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Updated</p>
                </div>
                <div className="bg-warning/10 border border-warning/30 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-warning">{summary?.totalRowsSkipped}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Not Found</p>
                </div>
                <div className="bg-error/10 border border-error/30 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-error">{summary?.totalRowsFailed}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Failed</p>
                </div>
              </div>

              {results?.filter((r) => r?.status !== 'success')?.length > 0 && (
                <div>
                  <p className="text-sm font-semibold text-foreground mb-2">Issues ({results?.filter((r) => r?.status !== 'success')?.length})</p>
                  <div className="space-y-1.5 max-h-48 overflow-y-auto">
                    {results?.filter((r) => r?.status !== 'success')?.map((r, idx) => (
                      <div key={idx} className="flex items-start gap-2 text-xs bg-muted/30 rounded-lg px-3 py-2">
                        <span className={`flex-shrink-0 mt-0.5 w-2 h-2 rounded-full ${statusColor(r?.status)}`} />
                        <span className="font-medium text-foreground min-w-[60px]">Row {r?.row}</span>
                        <span className="text-muted-foreground flex-1">{r?.instituteName}, {r?.city}</span>
                        <span className={`flex-shrink-0 px-1.5 py-0.5 rounded text-[10px] font-medium ${r?.status === 'not_found' ? 'bg-warning/20 text-warning' : 'bg-error/20 text-error'}`}>{statusLabel(r?.status)}</span>
                        <span className="text-muted-foreground flex-1">{r?.message}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-border flex-shrink-0 bg-muted/20">
          {result ? (
            <>
              <Button variant="outline" onClick={resetState}>Upload Another File</Button>
              <Button variant="default" onClick={handleClose}>Done</Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={handleClose} disabled={uploading}>Cancel</Button>
              <Button variant="default" onClick={handleUpload} disabled={!file || uploading} iconName={uploading ? 'Loader2' : 'Upload'}>
                {uploading ? 'Uploading…' : 'Upload Descriptions'}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkDescriptionUploadModal;
